import { ZERO_WEI } from '@kwenta/sdk/constants'
import { formatDollars, formatPercent } from '@kwenta/sdk/utils'
import { useRouter } from 'next/router'
import { FC, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import EyeIcon from 'assets/svg/app/eye.svg'
import InfoIcon from 'assets/svg/app/info-white.svg'
import Button from 'components/Button'
import { BaseCard } from 'components/Card/style'
import ColoredPrice from 'components/ColoredPrice'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { LabelPill } from 'components/Pill'
import { Body, Heading } from 'components/Text'
import ROUTES from 'constants/routes'
import { DASHBOARD_SECTION_HEIGHT_PX } from 'constants/ui'
import { SectionSeparator } from 'sections/futures/mobile'
import { setOpenModalWithParams } from 'state/app/reducer'
import { selectCrossMarginMarginInfo } from 'state/futures/snxPerpsV3/selectors'
import { ManageModalType, PerpsProvider } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import media from 'styles/media'

import AccountHealth from './AccountHealth'

type Props = {
	maskAsSensitive: boolean
	balanceChange?: number
	balanceChangePercent?: number
	toggleMaskAsSensitive: () => void
}

const AccountBalance: FC<Props> = memo(
	({ maskAsSensitive, balanceChange = 0, balanceChangePercent = 0, toggleMaskAsSensitive }) => {
		const { t } = useTranslation()
		const dispatch = useAppDispatch()
		const router = useRouter()
		const { availableMargin } = useAppSelector(selectCrossMarginMarginInfo)

		const handleModalAction = useCallback(
			(actionType: ManageModalType = ManageModalType.Deposit) => {
				dispatch(
					setOpenModalWithParams({
						type: 'futures_deposit_withdraw_cross_margin',
						params: { tab: actionType },
					})
				)
				router.push(ROUTES.Markets.Home(PerpsProvider.SNX_V3))
			},
			[dispatch, router]
		)

		return (
			<AccountBalanceContainer>
				<FlexDivCol>
					<HeadingContainer>
						<Body weight="semiBold" color="secondary">
							{t('dashboard.overview.account-balances.title')}
						</Body>
						<LabelPill $color="yellowGray" $size="small" $weight="bold">
							<StyledInfoIcon />
							{t('dashboard.overview.account-balances.cross-margin')}
						</LabelPill>
					</HeadingContainer>
					<StyledSectionSeparator />
					<FlexDivCol rowGap="5px">
						<FlexDivRowCentered justifyContent="flex-start" columnGap="10px">
							<StyledHeading variant="h3">
								{formatDollars(availableMargin, { maskAsSensitive })}
							</StyledHeading>
							<StyledEyeIcon onClick={toggleMaskAsSensitive} />
						</FlexDivRowCentered>
						<FlexDivRowCentered justifyContent="flex-start" columnGap="10px">
							<ColoredPrice priceChange={balanceChange >= 0 ? 'up' : 'down'}>
								{formatDollars(balanceChange, { maskAsSensitive })}
							</ColoredPrice>
							<LabelPill
								$color={balanceChange > 0 ? 'green' : 'red'}
								$size="small"
								$weight="semiBold"
							>
								{formatPercent(balanceChangePercent ?? ZERO_WEI, { maxDecimals: 2 })}
							</LabelPill>
						</FlexDivRowCentered>
					</FlexDivCol>
				</FlexDivCol>
				<AccountHealth maskAsSensitive={maskAsSensitive} />
				<FlexDivRow columnGap="10px">
					<Button size="small" fullWidth onClick={() => handleModalAction(ManageModalType.Deposit)}>
						{t('dashboard.overview.account-balances.deposit')}
					</Button>
					<Button
						size="small"
						fullWidth
						onClick={() => handleModalAction(ManageModalType.Withdraw)}
					>
						{t('dashboard.overview.account-balances.withdraw')}
					</Button>
				</FlexDivRow>
			</AccountBalanceContainer>
		)
	}
)

const StyledSectionSeparator = styled(SectionSeparator)`
	margin: 0 0 10px 0;
`

const StyledInfoIcon = styled(InfoIcon)`
	margin-right: 5px;
`

const AccountBalanceContainer = styled(BaseCard)`
	flex: 3;
	border-radius: 8px;
	min-width: 300px;
	max-width: 340px;
	height: ${DASHBOARD_SECTION_HEIGHT_PX};
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 20px;

	${media.lessThan('md')`
		min-width: 100%;
	`}
`

const StyledEyeIcon = styled(EyeIcon)`
	cursor: pointer;
`

const HeadingContainer = styled(FlexDivRowCentered)`
	padding: 0 0 10px;
`

const StyledHeading = styled(Heading)`
	font-family: ${(props) => props.theme.fonts.monoBold};
`

export default AccountBalance
